class CountdownTimerComponent extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.render();
  }

  static get observedAttributes() {
    return [
      'start-time',
      'end-time',
      'upcoming-title',
      'upcoming-description',
      'upcoming-title-color',
      'upcoming-description-color',
      'upcoming-title-size',
      'upcoming-description-size',
      'ending-title',
      'ending-description',
      'ending-title-color',
      'ending-description-color',
      'ending-title-size',
      'ending-description-size',
      'countdown-color',
      'label-color'
    ];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    this.updateAttribute(name, newValue);
  }

  updateAttribute(name, value) {
    switch (name) {
      case 'start-time':
        this.startTime = new Date(value).getTime();
        this.updateCountdown();
        break;
      case 'end-time':
        this.endTime = new Date(value).getTime();
        this.updateCountdown();
        break;
      case 'upcoming-title':
        this.upcomingTitle = value;
        this.updateContent();
        break;
      case 'upcoming-description':
        this.upcomingDescription = value;
        this.updateContent();
        break;
      case 'upcoming-title-color':
        this.upcomingTitleColor = value;
        this.updateStyles();
        break;
      case 'upcoming-description-color':
        this.upcomingDescriptionColor = value;
        this.updateStyles();
        break;
      case 'upcoming-title-size':
        this.upcomingTitleSize = value;
        this.updateStyles();
        break;
      case 'upcoming-description-size':
        this.upcomingDescriptionSize = value;
        this.updateStyles();
        break;
      case 'ending-title':
        this.endingTitle = value;
        this.updateContent();
        break;
      case 'ending-description':
        this.endingDescription = value;
        this.updateContent();
        break;
      case 'ending-title-color':
        this.endingTitleColor = value;
        this.updateStyles();
        break;
      case 'ending-description-color':
        this.endingDescriptionColor = value;
        this.updateStyles();
        break;
      case 'ending-title-size':
        this.endingTitleSize = value;
        this.updateStyles();
        break;
      case 'ending-description-size':
        this.endingDescriptionSize = value;
        this.updateStyles();
        break;
      case 'countdown-color':
        this.countdownColor = value;
        this.updateStyles();
        break;
      case 'label-color':
        this.labelColor = value;
        this.updateStyles();
        break;
    }
  }

  render() {
    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          width: 100%;
          padding: 2rem 0;
        }

        .container {
          width: 100%;
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 1.5rem;
          box-sizing: border-box;
          text-align: center;
        }

        .title {
          margin: 0 0 0.5rem 0;
          font-size: var(--title-font-size, 2.5rem);
          color: var(--title-color, #000);
          font-weight: 700;
        }

        .description {
          margin: 0 0 2rem 0;
          font-size: var(--description-font-size, 1.2rem);
          color: var(--description-color, #333);
        }

        .countdown {
          display: flex;
          justify-content: center;
          gap: 1rem;
        }

        .countdown-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          position: relative;
        }

        .countdown-value {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 45px;
          height: 50px;
          font-size: 2.5rem;
          font-weight: 700;
          background-color: #000;
          border-radius: 8px;
          color: var(--countdown-color, #fff);
          line-height: 1;
        }

        .countdown-separator {
          display: flex;
          align-items: center;
          height: 50px;
          font-size: 2.5rem;
          font-weight: 700;
          color: #000;
        }

        .countdown-label {
          font-size: 1rem;
          color: var(--label-color, #666);
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        @media (max-width: 767px) {
          .container {
            padding: 0 1rem;
          }

          .title {
            font-size: 1.75rem;
          }

          .description {
            font-size: 1rem;
          }

          .countdown {
            gap: 0.5rem;
          }

          .countdown-value {
            font-size: 1.75rem;
            min-width: 60px;
          }

          .countdown-separator {
            font-size: 1.75rem;
          }

          .countdown-label {
            font-size: 0.75rem;
          }
        }
      </style>
      <div class="container">
        <h2 class="title" id="title"></h2>
        <p class="description" id="description"></p>
        <div class="countdown" id="countdown">
          <div class="countdown-item">
            <span class="countdown-value" id="days">00</span>
            <span class="countdown-label">Days</span>
          </div>
          <div class="countdown-separator">:</div>
          <div class="countdown-item">
            <span class="countdown-value" id="hours">00</span>
            <span class="countdown-label">Hours</span>
          </div>
          <div class="countdown-separator">:</div>
          <div class="countdown-item">
            <span class="countdown-value" id="minutes">00</span>
            <span class="countdown-label">Minutes</span>
          </div>
          <div class="countdown-separator">:</div>
          <div class="countdown-item">
            <span class="countdown-value" id="seconds">00</span>
            <span class="countdown-label">Seconds</span>
          </div>
        </div>
      </div>
    `;
  }

  connectedCallback() {
    this.initializeComponent();
  }

  initializeComponent() {
    this.startTime = new Date(this.getAttribute('start-time')).getTime();
    this.endTime = new Date(this.getAttribute('end-time')).getTime();
    this.upcomingTitle = this.getAttribute('upcoming-title') || '';
    this.upcomingDescription = this.getAttribute('upcoming-description') || '';
    this.upcomingTitleColor = this.getAttribute('upcoming-title-color') || '#000';
    this.upcomingDescriptionColor = this.getAttribute('upcoming-description-color') || '#333';
    this.upcomingTitleSize = this.getAttribute('upcoming-title-size') || '2.5';
    this.upcomingDescriptionSize = this.getAttribute('upcoming-description-size') || '1.2';
    this.endingTitle = this.getAttribute('ending-title') || '';
    this.endingDescription = this.getAttribute('ending-description') || '';
    this.endingTitleColor = this.getAttribute('ending-title-color') || '#000';
    this.endingDescriptionColor = this.getAttribute('ending-description-color') || '#333';
    this.endingTitleSize = this.getAttribute('ending-title-size') || '2.5';
    this.endingDescriptionSize = this.getAttribute('ending-description-size') || '1.2';
    this.countdownColor = this.getAttribute('countdown-color') || '#000';
    this.labelColor = this.getAttribute('label-color') || '#666';

    const now = Date.now();
    if (now > this.endTime) {
      this.style.display = 'none';
      return;
    }

    this.updateContent();
    this.updateStyles();
    this.updateCountdown();
    this.startTimer();
  }

  updateContent() {
    const now = Date.now();
    const titleEl = this.shadowRoot.getElementById('title');
    const descriptionEl = this.shadowRoot.getElementById('description');

    if (now < this.startTime) {
      if (this.upcomingTitle && this.upcomingTitle.trim() !== '') {
        titleEl.textContent = this.upcomingTitle;
        titleEl.style.display = 'block';
      } else {
        titleEl.style.display = 'none';
      }
      if (this.upcomingDescription && this.upcomingDescription.trim() !== '') {
        descriptionEl.textContent = this.upcomingDescription;
        descriptionEl.style.display = 'block';
      } else {
        descriptionEl.style.display = 'none';
      }
    } else {
      if (this.endingTitle && this.endingTitle.trim() !== '') {
        titleEl.textContent = this.endingTitle;
        titleEl.style.display = 'block';
      } else {
        titleEl.style.display = 'none';
      }
      if (this.endingDescription && this.endingDescription.trim() !== '') {
        descriptionEl.textContent = this.endingDescription;
        descriptionEl.style.display = 'block';
      } else {
        descriptionEl.style.display = 'none';
      }
    }
  }

  updateStyles() {
    const now = Date.now();
    const container = this.shadowRoot.querySelector('.container');

    if (now < this.startTime) {
      container.style.setProperty('--title-color', this.upcomingTitleColor);
      container.style.setProperty('--description-color', this.upcomingDescriptionColor);
      container.style.setProperty('--title-font-size', this.upcomingTitleSize + 'rem');
      container.style.setProperty('--description-font-size', this.upcomingDescriptionSize + 'rem');
    } else {
      container.style.setProperty('--title-color', this.endingTitleColor);
      container.style.setProperty('--description-color', this.endingDescriptionColor);
      container.style.setProperty('--title-font-size', this.endingTitleSize + 'rem');
      container.style.setProperty('--description-font-size', this.endingDescriptionSize + 'rem');
    }

    container.style.setProperty('--countdown-color', this.countdownColor);
    container.style.setProperty('--label-color', this.labelColor);
  }

  updateCountdown() {
    const now = Date.now();
    let targetTime = this.startTime;

    if (now >= this.startTime) {
      targetTime = this.endTime;
    }

    const diff = targetTime - now;

    if (diff <= 0) {
      this.shadowRoot.getElementById('days').textContent = '00';
      this.shadowRoot.getElementById('hours').textContent = '00';
      this.shadowRoot.getElementById('minutes').textContent = '00';
      this.shadowRoot.getElementById('seconds').textContent = '00';
      return;
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    this.shadowRoot.getElementById('days').textContent = String(days).padStart(2, '0');
    this.shadowRoot.getElementById('hours').textContent = String(hours).padStart(2, '0');
    this.shadowRoot.getElementById('minutes').textContent = String(minutes).padStart(2, '0');
    this.shadowRoot.getElementById('seconds').textContent = String(seconds).padStart(2, '0');
  }

  startTimer() {
    this.timer = setInterval(() => {
      const now = Date.now();
      if (now > this.endTime) {
        this.style.display = 'none';
        if (this.timer) {
          clearInterval(this.timer);
        }
        return;
      }
      this.updateCountdown();
      this.updateContent();
      this.updateStyles();
    }, 1000);
  }

  disconnectedCallback() {
    if (this.timer) {
      clearInterval(this.timer);
    }
  }
}

if (!customElements.get('countdown-timer-component')) {
  customElements.define('countdown-timer-component', CountdownTimerComponent);
}